import { create } from 'zustand'
import axios from 'axios'

interface User {
  id: string
  email: string
  name: string
  picture?: string
}

interface AuthState {
  token: string | null
  user: User | null
  loading: boolean
  error: string | null
  setToken: (token: string | null) => void
  setUser: (user: User | null) => void
  fetchUser: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, name: string) => Promise<void>
  logout: () => void
}

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

export const useAuthStore = create<AuthState>((set, get) => ({
  token: localStorage.getItem('auth_token'),
  user: null,
  loading: false,
  error: null,

  setToken: (token) => {
    if (token) {
      localStorage.setItem('auth_token', token)
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
    } else {
      localStorage.removeItem('auth_token')
      delete axios.defaults.headers.common['Authorization']
    }
    set({ token })
  },

  setUser: (user) => set({ user }),

  fetchUser: async () => {
    const { token } = get()
    if (!token) return

    set({ loading: true, error: null })
    try {
      const response = await axios.get(`${API_BASE_URL}/api/user`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      set({ user: response.data, loading: false })
    } catch (error) {
      set({ error: 'Failed to fetch user', loading: false })
      console.error('Fetch user error:', error)
    }
  },

  login: async (email: string, password: string) => {
    set({ loading: true, error: null })
    try {
      const response = await axios.post(`${API_BASE_URL}/api/login`, {
        email,
        password,
      })
      const { token, user } = response.data
      get().setToken(token)
      set({ user, loading: false })
    } catch (error) {
      set({ error: 'Login failed', loading: false })
      throw error
    }
  },

  register: async (email: string, password: string, name: string) => {
    set({ loading: true, error: null })
    try {
      const response = await axios.post(`${API_BASE_URL}/api/register`, {
        email,
        password,
        name,
      })
      const { token, user } = response.data
      get().setToken(token)
      set({ user, loading: false })
    } catch (error) {
      set({ error: 'Registration failed', loading: false })
      throw error
    }
  },

  logout: () => {
    get().setToken(null)
    set({ user: null })
  },
}))
